<template>
    <activix-drag-upload
        :description="$t('composer.dropImage')"
        wrapper
        :disabled="isCustomImageActive"
        :display-item-card="false"
        @update:model-value="uploadImage"
    >
        <div class="w-full border border-gray-300 rounded bg-gray-150 relative">
            <composer-modal :modal-opened="modalOpened" @close="closeModal">
                <template #title v-if="modalLabel">
                    <div class="flex items-center">
                        <span class="text-2xl uppercase" v-text="modalLabel" />
                    </div>
                </template>

                <div
                    class="flex items-center h-full justify-center my-4"
                    v-if="modalItem.video && !massMailing && video?.leadId"
                >
                    <video-communication
                        class="w-1/2"
                        :ziggeo-id="initialVideoZiggeoId"
                        :processed="video.draftProcessed"
                        :user="video.user"
                        :lead-id="video?.leadId"
                        @video-cancelled="$emit('videoCancelled', $event)"
                    />
                </div>

                <div class="h-full overflow-auto" v-if="modalItem.inventory">
                    <inventory
                        context="tiptap"
                        initial-division="all"
                        @select-vehicle="addVehicleBrochure"
                    />
                </div>

                <composer-link
                    :selected-link="getSelectedLink"
                    :disabled="disabled"
                    @set-link="setLink"
                    v-if="modalItem.link"
                />

                <composer-image
                    :disabled="disabled"
                    :small="['xs', 'sm'].includes(getSize)"
                    @set-image="setImage"
                    @set-files="uploadImage"
                    v-if="modalItem.image"
                />
            </composer-modal>

            <div class="grid grid-cols-4">
                <div
                    class="flex flex-col w-full relative col-span-3 bg-white"
                    :style="{ height: composerHeight }"
                    :class="{
                        'bg-gray-100 text-gray-400 cursor-not-allowed': disabled,
                        'col-span-4 order-2': ['xs', 'sm'].includes(getSize),
                    }"
                    @click.stop="focus"
                >
                    <div class="h-full overflow-y-auto">
                        <toolbar :editor="editor" :align-icon="alignIcon" :disabled="disabled" />
                        <slot name="after-toolbar" />
                        <div class="tiptap-wrapper px-6 pt-6">
                            <editor-content
                                id="editorBox"
                                data-testid="tiptap-editor"
                                :editor="editor"
                            />
                            <slot name="after-editor-content" />
                        </div>

                        <!-- ORIGINAL EMAIL -->
                        <div class="px-6" v-if="originalCommunication">
                            <table
                                style="border: solid 1px #f2f2f2; width: 100%; margin: 12px auto"
                            >
                                <thead
                                    style="
                                        border-bottom: solid 1px #f2f2f2;
                                        background-color: #f9f9f9;
                                    "
                                >
                                    <tr>
                                        <th
                                            style="font-weight: 400; padding: 6px"
                                            v-text="$t('responseLeadXpress.originalCommunication')"
                                        />
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td
                                            style="padding: 6px"
                                            colspan="2"
                                            v-html="originalCommunication"
                                        />
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- HISTORY -->
                        <div class="px-6" v-if="history && addLastEmailBody">
                            <div
                                class="flex justify-between items-center space-x-4"
                                :class="historyShown ? 'my-6' : 'mt-6'"
                            >
                                <hr class="w-full h-px border-none bg-gray-300" />
                                <button
                                    class="flex-shrink-0 flex items-center px-2 py-1 text-gray-500"
                                    @click="historyShownToggle"
                                    v-text="
                                        historyShown
                                            ? $t('composer.hideHistory')
                                            : $t('composer.showHistory')
                                    "
                                />
                                <hr class="w-full h-px border-none bg-gray-300" />
                            </div>

                            <div class="px-2 text-gray-600" v-html="history" v-if="historyShown" />
                        </div>
                    </div>

                    <!-- RESIZE BAR -->
                    <div
                        class="flex justify-between items-center sticky bottom-0 inset-x-0 p-2 border-t border-r border-gray-300 bg-gray-100 cursor-ns-resize"
                        @mousedown="initResize"
                        v-if="!hideResizeBar"
                    >
                        <div
                            class="flex-1 text-sm text-gray-600 h-4 flex items-center select-none"
                            v-if="editor"
                        >
                            <span v-text="characterCountText" />
                        </div>

                        <div
                            class="flex-1 flex flex-col items-center h-full justify-center space-y-2px"
                        >
                            <div class="w-6 h-2px rounded-full bg-gray-500" />
                            <div class="w-6 h-2px rounded-full bg-gray-500" />
                        </div>

                        <div class="flex-1" />
                    </div>
                </div>

                <div
                    class="w-full h-full max-h-192 p-3 space-y-3 border-l border-gray-300 bg-gray-150"
                    :class="{ 'order-1 col-span-4': ['xs', 'sm'].includes(getSize) }"
                >
                    <!-- small editor -->
                    <div
                        class="flex overflow-x-auto"
                        :class="getSize === 'sm' ? 'space-x-2 flex-no-wrap' : 'flex-wrap gap-1'"
                        v-if="['xs', 'sm'].includes(getSize)"
                    >
                        <div class="flex" :class="{ 'flex-1': getSize === 'sm' }">
                            <activix-select
                                :class="size === 'md' ? 'w-56' : 'w-32'"
                                :disabled="disabled"
                                :options="fontFamilies"
                                :value="fontFamilyModel"
                                @input="fontFamilyModel = $event"
                            />
                            <activix-select
                                :class="size === 'md' ? 'w-56' : 'w-20'"
                                :disabled="disabled"
                                :options="allFontSize"
                                :value="fontSizeModel"
                                @input="fontSizeModel = $event"
                            />
                        </div>
                        <div class="flex h-10" :class="getSize === 'sm' ? 'flex-1' : 'ml-2'">
                            <composer-button
                                :disabled="disabled"
                                color="light-gray"
                                icon="regular/text-bold"
                                :tooltip-content="$t('composer.tools.bold')"
                                :selected="isActive('bold')"
                                @click="editor.chain().focus().toggleBold().run()"
                            />
                            <composer-button
                                :disabled="disabled"
                                color="light-gray"
                                icon="regular/text-italic"
                                :tooltip-content="$t('composer.tools.italic')"
                                :selected="isActive('italic')"
                                @click="editor.chain().focus().toggleItalic().run()"
                            />
                            <composer-button
                                :disabled="disabled"
                                color="light-gray"
                                icon="regular/text-underline"
                                :tooltip-content="$t('composer.tools.underline')"
                                :selected="isActive('underline')"
                                @click="editor.chain().focus().toggleUnderline().run()"
                            />
                            <composer-button
                                :disabled="disabled"
                                color="light-gray"
                                icon="regular/text-strike-through"
                                :tooltip-content="$t('composer.tools.strikethrough')"
                                :selected="isActive('strike')"
                                @click="editor.chain().focus().toggleStrike().run()"
                            />
                        </div>
                        <div class="flex h-10" :class="getSize === 'sm' ? 'flex-1' : 'ml-2'">
                            <color-picker
                                class="mr-1"
                                classes="flex-1 border-none"
                                button-color="light-gray"
                                :tooltip-content="$t('composer.tools.textColor')"
                                :selected-color="getColor"
                                @input="setColorText"
                            />

                            <color-picker
                                classes="flex-1 border-none"
                                button-color="light-gray"
                                :tooltip-content="$t('composer.tools.textBackground')"
                                :selected-color="getBgColor"
                                @input="setColorBackground"
                            >
                                <template #icon>
                                    <icon name="regular/color-bucket-1" />
                                </template>
                            </color-picker>

                            <composer-button
                                :disabled="disabled"
                                icon="regular/eraser"
                                color="light-gray"
                                :tooltip-content="$t('composer.tools.erase')"
                                @click="editor.chain().focus().unsetAllMarks().run()"
                            />
                        </div>

                        <div class="flex h-10">
                            <composer-button
                                :icon="alignIcon"
                                dropdown
                                :dropdown-opened="alignSmallDropDownOpened"
                                color="light-gray"
                                :tooltip-content="$t('composer.tools.align')"
                                @click="alignSmallDropDownOpened = !alignSmallDropDownOpened"
                                v-if="getSize !== 'sm'"
                            />
                            <div
                                class="bg-gray-100 rounded flex"
                                :class="{ 'absolute top-0 -mt-12 -ml-14 shadow': getSize === 'xs' }"
                                v-if="alignSmallDropDownOpened || getSize === 'sm'"
                            >
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/paragraph-left-align"
                                    :tooltip-content="$t('composer.tools.alignLeft')"
                                    color="light-gray"
                                    :selected="isActive({ textAlign: 'left' })"
                                    @click="onClickAlign('left')"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/paragraph-center-align"
                                    :tooltip-content="$t('composer.tools.alignCenter')"
                                    color="light-gray"
                                    :selected="isActive({ textAlign: 'center' })"
                                    @click="onClickAlign('center')"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/paragraph-right-align"
                                    :tooltip-content="$t('composer.tools.alignRight')"
                                    color="light-gray"
                                    :selected="isActive({ textAlign: 'right' })"
                                    @click="onClickAlign('right')"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/paragraph-justified-align"
                                    :tooltip-content="$t('composer.tools.justify')"
                                    color="light-gray"
                                    :selected="isActive({ textAlign: 'justify' })"
                                    @click="onClickAlign('justify')"
                                />
                            </div>
                        </div>

                        <div class="flex h-10" :class="getSize === 'sm' ? 'flex-1' : ''">
                            <composer-button
                                :icon="listIcon"
                                dropdown
                                :dropdown-opened="listSmallDropDownOpened"
                                color="light-gray"
                                :tooltip-content="$t('composer.tools.list')"
                                @click="listSmallDropDownOpened = !listSmallDropDownOpened"
                                v-if="getSize !== 'sm'"
                            />
                            <div
                                class="bg-gray-100 rounded flex"
                                :class="{ 'absolute top-0 -mt-12 -ml-14 shadow': getSize === 'xs' }"
                                v-if="listSmallDropDownOpened || getSize === 'sm'"
                            >
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/list-bullets"
                                    :tooltip-content="$t('composer.tools.bulletList')"
                                    color="light-gray"
                                    :selected="isActive('bulletList')"
                                    @click="
                                        listSmallDropDownOpened = false;
                                        editor.chain().focus().toggleBulletList().run();
                                    "
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/list-numbers"
                                    :tooltip-content="$t('composer.tools.orderedList')"
                                    color="light-gray"
                                    :selected="isActive('orderedList')"
                                    @click="
                                        listSmallDropDownOpened = false;
                                        editor.chain().focus().toggleOrderedList().run();
                                    "
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/indent-left"
                                    :tooltip-content="$t('composer.tools.blockQuote')"
                                    color="light-gray"
                                    :selected="isActive('blockquote')"
                                    @click="
                                        listSmallDropDownOpened = false;
                                        editor.chain().focus().toggleBlockquote().run();
                                    "
                                />
                            </div>
                        </div>

                        <div class="flex h-10" :class="getSize === 'sm' ? 'flex-2' : 'ml-2'">
                            <composer-button
                                :disabled="disabled"
                                color="light-gray"
                                icon="regular/meeting-camera"
                                :tooltip-content="
                                    video?.ziggeoId
                                        ? $t('composer.tools.isVideo')
                                        : $t('composer.tools.video')
                                "
                                :selected="!!video?.ziggeoId"
                                @click="openVideoPresentation"
                                v-if="!massMailing && video"
                            />
                            <composer-button
                                :disabled="disabled"
                                color="light-gray"
                                icon="regular/car-actions-search-1"
                                :tooltip-content="$t('composer.tools.insertVehicle')"
                                @click="openInventorySelection"
                                v-if="canInsertVehicleButton"
                            />
                            <composer-button
                                :disabled="disabled"
                                color="light-gray"
                                icon="regular/hyperlink"
                                :tooltip-content="$t('composer.tools.link')"
                                @click="openLink"
                            />
                            <composer-button
                                :disabled="disabled"
                                color="light-gray"
                                icon="regular/common-file-horizontal-image"
                                :tooltip-content="$t('composer.tools.image')"
                                @click="openImage"
                            />
                            <activix-select
                                height-class-name="h-96"
                                width-class-name="min-w-72"
                                :options="attributesOption()"
                                :filterable="true"
                                :same-width="false"
                                :value="[]"
                                @input="addAttribute"
                                v-if="lead && !massMailing && showAttributes"
                            >
                                <template #trigger>
                                    <composer-button
                                        :disabled="disabled"
                                        :selected="showPopper"
                                        color="light-gray"
                                        icon="regular/text-input-area"
                                        :tooltip-content="$t('composer.tools.attributes')"
                                    />
                                </template>
                            </activix-select>

                            <activix-tooltip
                                :content="$t('templates.goToAdvanced')"
                                v-if="size === 'md'"
                            >
                                <button
                                    class="flex justify-center items-center p-2 rounded text-xl text-gray-600 | hover:bg-gray-300"
                                    @click="setPreferredSize('md')"
                                >
                                    <icon name="regular/synchronize-arrows-square-1" />
                                </button>
                            </activix-tooltip>
                        </div>
                    </div>

                    <!-- Rich editor -->
                    <template v-else>
                        <div class="space-y-3 relative">
                            <activix-tooltip :content="$t('templates.goToBasic')">
                                <button
                                    class="absolute right-0 top-1 flex justify-center items-center p-1.5 rounded-sm text-xl text-gray-600 | hover:bg-gray-300"
                                    @click="setPreferredSize('sm')"
                                >
                                    <icon name="regular/synchronize-arrows-square-1" />
                                </button>
                            </activix-tooltip>

                            <activix-label :label="$t('composer.label.styling')" />
                            <div class="flex justify-between items-stretch space-x-3">
                                <activix-select
                                    class="w-2/3"
                                    :disabled="disabled"
                                    :options="fontFamilies"
                                    :value="fontFamilyModel"
                                    @input="fontFamilyModel = $event"
                                />
                                <activix-select
                                    class="w-1/3"
                                    :disabled="disabled"
                                    :options="allFontSize"
                                    :value="fontSizeModel"
                                    @input="fontSizeModel = $event"
                                />
                            </div>

                            <div class="flex items-stretch p-1 space-x-1 rounded bg-gray-300">
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/text-bold"
                                    :tooltip-content="$t('composer.tools.bold')"
                                    :selected="isActive('bold')"
                                    @click="editor.chain().focus().toggleBold().run()"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/text-italic"
                                    :tooltip-content="$t('composer.tools.italic')"
                                    :selected="isActive('italic')"
                                    @click="editor.chain().focus().toggleItalic().run()"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/text-underline"
                                    :tooltip-content="$t('composer.tools.underline')"
                                    :selected="isActive('underline')"
                                    @click="editor.chain().focus().toggleUnderline().run()"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/text-strike-through"
                                    :tooltip-content="$t('composer.tools.strikethrough')"
                                    :selected="isActive('strike')"
                                    @click="editor.chain().focus().toggleStrike().run()"
                                />
                            </div>

                            <div class="flex items-stretch space-x-3">
                                <color-picker
                                    classes="flex-1 border-none"
                                    button-color="dark-gray"
                                    :tooltip-content="$t('composer.tools.textColor')"
                                    :selected-color="getColor"
                                    @input="setColorText"
                                />

                                <color-picker
                                    classes="flex-1 border-none"
                                    button-color="dark-gray"
                                    :tooltip-content="$t('composer.tools.textBackground')"
                                    :selected-color="getBgColor"
                                    @input="setColorBackground"
                                >
                                    <template #icon>
                                        <icon name="regular/color-bucket-1" />
                                    </template>
                                </color-picker>

                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/eraser"
                                    :tooltip-content="$t('composer.tools.erase')"
                                    @click="editor.chain().focus().unsetAllMarks().run()"
                                />
                            </div>
                        </div>

                        <hr class="w-full h-px border-none bg-gray-300" />

                        <div class="space-y-3">
                            <activix-label :label="$t('composer.label.alignment')" />
                            <div class="flex items-stretch p-1 space-x-1 rounded bg-gray-300">
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/paragraph-left-align"
                                    :tooltip-content="$t('composer.tools.alignLeft')"
                                    :selected="isActive({ textAlign: 'left' })"
                                    @click="editor.chain().focus().setTextAlign('left').run()"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/paragraph-center-align"
                                    :tooltip-content="$t('composer.tools.alignCenter')"
                                    :selected="isActive({ textAlign: 'center' })"
                                    @click="editor.chain().focus().setTextAlign('center').run()"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/paragraph-right-align"
                                    :tooltip-content="$t('composer.tools.alignRight')"
                                    :selected="isActive({ textAlign: 'right' })"
                                    @click="editor.chain().focus().setTextAlign('right').run()"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/paragraph-justified-align"
                                    :tooltip-content="$t('composer.tools.justify')"
                                    :selected="isActive({ textAlign: 'justify' })"
                                    @click="editor.chain().focus().setTextAlign('justify').run()"
                                />
                            </div>

                            <div class="flex items-stretch p-1 space-x-1 rounded bg-gray-300">
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/list-bullets"
                                    :tooltip-content="$t('composer.tools.bulletList')"
                                    :selected="isActive('bulletList')"
                                    @click="editor.chain().focus().toggleBulletList().run()"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/list-numbers"
                                    :tooltip-content="$t('composer.tools.orderedList')"
                                    :selected="isActive('orderedList')"
                                    @click="editor.chain().focus().toggleOrderedList().run()"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/indent-left"
                                    :tooltip-content="$t('composer.tools.blockQuote')"
                                    :selected="isActive('blockquote')"
                                    @click="editor.chain().focus().toggleBlockquote().run()"
                                />
                            </div>
                        </div>

                        <hr class="w-full h-px border-none bg-gray-300" />

                        <div class="space-y-3">
                            <activix-label :label="$t('composer.label.insertion')" />
                            <div class="flex items-stretch space-x-3">
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/meeting-camera"
                                    :tooltip-content="
                                        video?.ziggeoId
                                            ? $t('composer.tools.isVideo')
                                            : $t('composer.tools.video')
                                    "
                                    :selected="!!video?.ziggeoId"
                                    @click="openVideoPresentation"
                                    v-if="!massMailing && video"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/car-actions-search-1"
                                    :tooltip-content="$t('composer.tools.insertVehicle')"
                                    @click="openInventorySelection"
                                    v-if="canInsertVehicleButton"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/hyperlink"
                                    :tooltip-content="$t('composer.tools.link')"
                                    @click="openLink"
                                />
                                <composer-button
                                    :disabled="disabled"
                                    icon="regular/common-file-horizontal-image"
                                    :tooltip-content="$t('composer.tools.image')"
                                    @click="openImage"
                                />
                            </div>
                            <activix-select
                                class="w-full"
                                height-class-name="h-96"
                                width-class-name="min-w-72"
                                :options="attributesOption()"
                                :filterable="true"
                                :same-width="false"
                                :value="[]"
                                @input="addAttribute"
                                v-if="lead && !massMailing && showAttributes"
                            >
                                <template #trigger>
                                    <composer-button
                                        :disabled="disabled"
                                        :selected="showPopper"
                                        class="mt-6 w-full"
                                    >
                                        <div class="space-x-3">
                                            <icon class="text-lg" name="regular/text-input-area" />
                                            <span v-text="$t('composer.tools.attributes')" />
                                        </div>
                                    </composer-button>
                                </template>
                            </activix-select>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </activix-drag-upload>
</template>

<script>
    import Lead from '@/entities/Lead.js';
    import { useVehicleBrochure } from '@/hooks/useVehicleBrochure.ts';

    // Mixins
    import EmailCompose from '@/mixins/EmailCompose.js';
    // Components
    import Toolbar from '@/components/composer/Toolbar.vue';
    import ComposerButton from '@/components/composer/ComposerButton.vue';
    import ComposerImage from '@/components/composer/ComposerImage.vue';
    import ComposerLink from '@/components/composer/ComposerLink.vue';
    import ComposerModal from '@/components/composer/ComposerModal.vue';
    import VideoCommunication from '@/components/lead/VideoCommunication.vue';
    import ActivixLabel from '@/components/elements/ActivixLabel.vue';
    import ColorPicker from '@/components/lead/inputs/ColorPicker.vue';
    import { useDashboardStore } from '@/store/modules/dashboard/store.js';
    import Inventory from '@/views/Inventory.vue';
    import { ActivixSelect } from '@/components/activix-ui/index.ts';

    // TipTap
    import { Editor, EditorContent } from '@tiptap/vue-3';
    import VehicleBrochureNode from '@/components/composer/extensions/VehicleBrochureNode.js';
    import {
        CharacterCount,
        Color,
        CustomColorBg,
        CustomFontSize,
        CustomImage,
        FontFamily,
        Highlight,
        Link,
        StarterKit,
        TextAlign,
        TextStyle,
        Underline,
        StyledTable,
        StyledTableCell,
        StyledTableHeader,
        StyledTableRow,
    } from '@/components/composer/utils/tiptap-extensions.js';
    import { debounce } from 'lodash-es';
    import { normalizeWebUrl } from '@/components/composer/utils/normalizeWebUrl.ts';

    // Pinia
    import { mapState } from 'pinia';
    import ActivixDragUpload from '@/components/elements/ActivixDragUpload.vue';
    import { useAccountStore } from '@/store/modules/account/store.js';
    import { useUserStore } from '@/store/modules/user/store.js';

    export {
        encodeVehicleBrochurePlaceholders,
        decodeVehicleBrochurePlaceholders,
    } from '@/hooks/useVehicleBrochure.ts';

    export const sizes = ['xs', 'sm', 'md'];

    Object.assign(EditorContent, {
        compatConfig: {
            MODE: 3,
            RENDER_FUNCTION: false,
        },
    });

    export default {
        name: 'ActivixTipTap',

        compatConfig: {
            COMPONENT_V_MODEL: false,
        },

        emits: ['modelValueApplied', 'update:modelValue', 'videoCancelled'],

        components: {
            Inventory,
            ActivixDragUpload,
            ActivixLabel,
            ActivixSelect,
            ColorPicker,
            ComposerButton,
            ComposerImage,
            ComposerLink,
            ComposerModal,
            EditorContent,
            Toolbar,
            VideoCommunication,
        },

        mixins: [EmailCompose],

        props: {
            minHeight: {
                type: Number,
                default: 200,
            },
            maxHeight: {
                type: Number,
                default: 500,
            },
            size: {
                type: String,
                default: 'md',
            },
            modelValue: {
                type: [String, Boolean],
                default: '',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            history: {
                type: String,
                default: '',
            },
            addLastEmailBody: {
                type: Boolean,
                default: false,
            },
            originalCommunication: {
                type: String,
                default: '',
            },
            locale: {
                type: String,
                default: '',
            },
            showAttributes: {
                type: Boolean,
                default: false,
            },
            showBrochure: {
                type: Boolean,
                default: false,
            },
            lead: {
                type: Lead,
            },
            video: {
                type: Object,
            },
            sender: {
                type: Object,
                default: null,
            },
            hideResizeBar: {
                type: Boolean,
                default: false,
            },
        },

        provide() {
            return {
                events: this.provide.events,
            };
        },

        data() {
            return {
                characterCountText: '',
                modalOpened: false,
                modalItem: {
                    link: false,
                    image: false,
                    video: false,
                    inventory: false,
                },
                alignSmallDropDownOpened: false,
                listSmallDropDownOpened: false,
                height: this.minHeight,
                initDragPos: 0,
                editor: null,
                historyShown: false,
                showPopper: false,
                provide: {
                    events: {
                        onClickAlign: this.onClickAlign,
                        openLink: this.openLink,
                        setColorBackground: this.setColorBackground,
                        setColorText: this.setColorText,
                        isActive: this.isActive,
                    },
                },
                preferredSize: this.size,
            };
        },

        computed: {
            ...mapState(useAccountStore, ['contextAccount']),
            ...mapState(useUserStore, ['authUser']),
            ...mapState(useDashboardStore, ['massMailing']),

            fontFamilies() {
                return [
                    { value: 'system-ui', label: 'system-ui' },
                    { value: 'Arial', label: 'Arial' },
                    { value: 'Tahoma', label: 'Tahoma' },
                    { value: 'Roboto', label: 'Roboto' },
                ];
            },

            allFontSize() {
                return [
                    { value: '8', label: '8' },
                    { value: '12', label: '12' },
                    { value: '14', label: '14' },
                    { value: '16', label: '16' },
                    { value: '24', label: '24' },
                    { value: '36', label: '36' },
                    { value: '48', label: '48' },
                ];
            },

            composerHeight() {
                return `${this.height + (this.hideResizeBar ? 0 : 8)}px`;
            },

            canInsertVehicleButton() {
                return (
                    !this.massMailing &&
                    this.showBrochure &&
                    this.authUser.hasAccessTo('dashboards.inventory') &&
                    this.$feature.isEnabled('vehicle-brochure', false)
                );
            },

            fontSizeModel: {
                get() {
                    const fontSize = parseInt(this.fontSize(), 10);
                    return fontSize ? `${fontSize}` : '12';
                },

                set(value) {
                    this.editor.chain().focus().setFontSize(`${value}px`).run();
                },
            },

            fontFamilyModel: {
                get() {
                    return this.fontFamily();
                },

                set(value) {
                    this.editor.chain().focus().setFontFamily(value).run();
                },
            },

            attributePayload() {
                return {
                    sender: this.sender || this.authUser,
                    lead: this.lead,
                    locale: this.locale || this.$i18n.getContentLanguage(),
                    prefix: '',
                    massMailing: this.massMailing,
                    users: this.contextAccount.activeUsers,
                    html: false,
                };
            },

            getColor() {
                return this.editor?.getAttributes('textStyle').color || '#000000';
            },

            getBgColor() {
                return this.editor?.getAttributes('textStyle').backgroundColor || '#FFFFFF';
            },

            initialVideoZiggeoId() {
                return this.video?.draftZiggeoId;
            },

            getSelectedLink() {
                if (this.editor?.isActive?.('image')) {
                    return this.editor.getAttributes('image')?.href;
                }

                return this.editor?.getAttributes('link')?.href;
            },

            alignIcon() {
                if (this.isActive({ textAlign: 'center' })) {
                    return 'regular/paragraph-center-align';
                }
                if (this.isActive({ textAlign: 'right' })) {
                    return 'regular/paragraph-right-align';
                }
                if (this.isActive({ textAlign: 'justify' })) {
                    return 'regular/paragraph-justified-align';
                }

                return 'regular/paragraph-left-align';
            },

            listIcon() {
                if (this.isActive('bulletList')) {
                    return 'regular/list-bullets';
                }
                if (this.isActive('orderedList')) {
                    return 'regular/list-numbers';
                }
                if (this.isActive('blockquote')) {
                    return 'regular/indent-left';
                }

                return 'regular/list-bullets';
            },

            modalLabel() {
                if (this.modalItem.link) {
                    return this.$t('composer.insertLink');
                }

                if (this.modalItem.image) {
                    return this.$t('composer.uploadImage');
                }

                if (this.modalItem.video) {
                    return this.$t('communications.video.title');
                }

                if (this.modalItem.inventory) {
                    return this.$t('clientCard.vehicles.stockTitle');
                }

                return '';
            },

            isCustomImageActive() {
                if (!this.editor) {
                    return false;
                }

                return this.editor.isActive('image');
            },

            getSize() {
                return this.preferredSize && !['xs', 'sm'].includes(this.size)
                    ? this.preferredSize
                    : this.size;
            },
        },

        watch: {
            disabled(disabled) {
                this.editor?.setEditable(!disabled);
            },

            modelValue(newValue) {
                this.setValue(newValue);
            },
        },

        methods: {
            attributesOption() {
                return this.templateAttributes
                    .filter(
                        (attribute) =>
                            !['other', 'commercial', 'exchange_vehicle'].includes(
                                attribute['name-value']
                            )
                    )
                    .map((category) => {
                        return {
                            label: category.name,
                            options: category.tags.map((tag) => {
                                const value = this.lead.id
                                    ? this.getAttributeValue({
                                          group: category,
                                          attribute: tag,
                                          ...this.attributePayload,
                                      })
                                    : '';

                                return {
                                    label: tag.text,
                                    value: value ? `${value}||${tag.tag}` : tag.tag, // Workaround to avoid duplicate values.
                                    disabled: !value,
                                };
                            }),
                        };
                    });
            },

            fontFamily() {
                if (!this.editor) {
                    return '';
                }

                let selectedHeader = 'system-ui';

                this.fontFamilies.forEach((family) => {
                    if (this.editor.isActive('textStyle', { fontFamily: family.value })) {
                        selectedHeader = family.value;
                    }
                });

                return selectedHeader;
            },

            fontSize() {
                if (!this.editor) {
                    return '';
                }

                let selectedFontSize = '14';

                this.allFontSize.forEach((size) => {
                    if (this.editor.isActive('textStyle', { fontSize: `${size.value}px` })) {
                        selectedFontSize = size.value;
                    }
                });

                return selectedFontSize;
            },

            setValue(newValue) {
                if (!this.editor) {
                    return;
                }

                const baseValue = typeof newValue === 'string' ? newValue : '';
                const { encodeVehicleBrochurePlaceholders } = useVehicleBrochure();
                const preparedValue = encodeVehicleBrochurePlaceholders(baseValue);

                if (preparedValue !== this.editor.getHTML()) {
                    this.editor.commands.setContent(preparedValue);
                }

                this.$emit?.('modelValueApplied', baseValue);
            },

            openModal() {
                this.modalOpened = true;
            },

            closeModal() {
                this.modalOpened = false;
                this.modalItem.video = false;
                this.modalItem.link = false;
                this.modalItem.image = false;
                this.modalItem.inventory = false;
            },

            addAttribute(value) {
                value = value.split('||')[0] || null;

                if (value) {
                    this.insertContent(value);
                    this.focus();
                }
            },

            addVehicleBrochure(selectedVehicle) {
                const { getVehicleBrochure, minify } = useVehicleBrochure();
                const html = minify(getVehicleBrochure(selectedVehicle, this.locale));
                const { selection } = this.editor.state;
                const shouldInsertParagraphBefore =
                    selection.$from.parentOffset === 0 && selection.$from.index() === 0;
                const content = [
                    ...(shouldInsertParagraphBefore ? [{ type: 'paragraph' }] : []),
                    {
                        type: 'vehicleBrochure',
                        attrs: { html },
                    },
                    { type: 'paragraph' },
                ];

                this.editor.chain().focus().insertContent(content).run();

                this.closeModal();
            },

            async uploadImage(files) {
                if (!files || !files[0]) {
                    return;
                }

                const payload = new FormData();
                payload.append('image', files[0]);

                try {
                    const response = await this.$axios.post('v1/auth-user/images', payload);
                    this.setImage(response.data.data);
                } catch (error) {
                    this.$notify.warning(this.$t('templates.errorUploading'));
                    throw error;
                }
            },

            getClipboardImageFiles(event) {
                const items = Array.from(event?.clipboardData?.items || []);

                return items
                    .filter((item) => item.kind === 'file' && item.type?.startsWith('image/'))
                    .map((item) => item.getAsFile())
                    .filter(Boolean);
            },

            handlePasteImage(_view, event) {
                const files = this.getClipboardImageFiles(event);

                if (!files.length) {
                    return false;
                }

                event.preventDefault();
                this.uploadImage(files).catch(() => {});

                return true;
            },

            openLink() {
                this.modalItem.link = true;
                this.openModal();
            },

            openImage() {
                this.modalItem.image = true;
                this.openModal();
            },

            openVideoPresentation() {
                this.height = 600;
                this.modalItem.video = true;
                this.openModal();
            },

            openInventorySelection() {
                this.height = 600;
                this.modalItem.inventory = true;
                this.openModal();
            },

            onClickAlign(type) {
                this.alignSmallDropDownOpened = false;
                this.editor.chain().focus().setTextAlign(type).run();
            },

            initResize(e) {
                this.initDragPos = e.clientY;
                this.initHeight = this.height;
                const win = globalThis.window;
                win?.addEventListener('mousemove', this.resize, false);
                win?.addEventListener('mouseup', this.stopResize, false);
            },

            stopResize() {
                const win = globalThis.window;
                win?.removeEventListener('mousemove', this.resize, false);
                win?.removeEventListener('mouseup', this.stopResize, false);
            },

            resize(e) {
                const pos = this.initHeight - (this.initDragPos - e.clientY);

                if (pos > this.maxHeight || pos < this.minHeight) {
                    return;
                }

                this.height = pos;
            },

            focus() {
                if (!this.editor) {
                    return;
                }

                this.editor.chain().focus().run();
            },

            isActive(name) {
                if (!this.editor) {
                    return false;
                }

                return this.editor.isActive(name);
            },

            setImage(url) {
                if (!url) {
                    return;
                }

                this.editor.chain().focus().setImage({ src: url }).run();
                this.closeModal();
            },

            setLink(link) {
                const editor = this.editor.chain();

                if (!link) {
                    if (this.editor.isActive('image')) {
                        editor.focus().updateAttributes('image', { href: null }).run();
                        this.closeModal();
                        return;
                    }

                    editor.focus().unsetLink().run();
                    this.closeModal();
                    return;
                }

                const normalizedLink = normalizeWebUrl(link);

                if (!normalizedLink) {
                    return;
                }

                if (this.editor.isActive('image')) {
                    editor.focus().updateAttributes('image', { href: normalizedLink }).run();
                    this.closeModal();
                    return;
                }

                if (this.editor.view.state.selection.empty) {
                    this.insertContent(`<a href="${normalizedLink}">${normalizedLink}</a>`);
                    this.closeModal();
                    return;
                }

                editor.focus().extendMarkRange('link').setLink({ href: normalizedLink }).run();
                this.closeModal();
            },

            historyShownToggle() {
                this.historyShown = !this.historyShown;
            },

            updateHtml(editor) {
                const { decodeVehicleBrochurePlaceholders } = useVehicleBrochure();
                this.$emit(
                    'update:modelValue',
                    decodeVehicleBrochurePlaceholders(editor.getHTML())
                );
            },

            setColorBackground(event) {
                this.editor.chain().focus().setBgColor(event).run();
            },

            setColorText(event) {
                this.editor.chain().focus().setColor(event).run();
            },

            replaceContent(content) {
                this.clearContent(true);
                this.insertContent(content);
            },

            clearContent(refresh = false) {
                if (!this.editor) {
                    return;
                }

                this.editor.commands.clearContent(refresh);
            },

            insertContent(content) {
                if (!this.editor) {
                    return;
                }

                this.editor.commands.insertContent(content);
            },

            init() {
                const updateHtmlFn = this.updateHtml;
                const setCharactersCountFn = this.setCharactersCount;
                const { encodeVehicleBrochurePlaceholders } = useVehicleBrochure();

                this.editor = new Editor({
                    editable: !this.disabled,
                    content: encodeVehicleBrochurePlaceholders(this.modelValue || ''),
                    editorProps: {
                        handlePaste: this.handlePasteImage,
                    },
                    extensions: [
                        CharacterCount.configure({
                            mode: 'textSize',
                        }),
                        Color,
                        CustomColorBg,
                        CustomFontSize,
                        FontFamily,
                        Highlight,
                        TextStyle,
                        Underline,
                        CustomImage,
                        StyledTable.configure({
                            resizable: true,
                        }),
                        StyledTableRow,
                        StyledTableHeader,
                        StyledTableCell,
                        VehicleBrochureNode,
                        Link.configure({
                            openOnClick: false,
                            autolink: true,
                            defaultProtocol: 'https',
                            protocols: ['http', 'https'],
                        }),
                        StarterKit.configure({
                            dropcursor: { class: 'bg-blue-200', width: 2 },
                        }),
                        TextAlign.configure({ types: ['heading', 'paragraph'] }),
                    ],
                    onUpdate({ editor }) {
                        updateHtmlFn(editor);
                        setCharactersCountFn();
                    },
                });
            },

            setPreferredSize(size) {
                localStorage.setItem('editor-preferred-size', size);
                this.preferredSize = size;
            },

            setCharactersCount() {
                this.characterCountText =
                    this.editor.storage.characterCount.characters() > 0
                        ? this.$t(
                              'composer.characters',
                              [this.editor.storage.characterCount.characters()],
                              this.editor.storage.characterCount.characters()
                          )
                        : '';
            },
        },

        mounted() {
            this.init();
            this.setCharactersCount();
            this.setPreferredSize(localStorage.getItem('editor-preferred-size'));
        },

        created() {
            this.setValue = debounce(this.setValue, 300);
            this.setCharactersCount = debounce(this.setCharactersCount, 300);
        },

        beforeUnmount() {
            this.editor.destroy();
        },
    };
</script>

<style lang="less">
    .tiptap-wrapper {
        // ProseMirror adds a trailing <br> to every block node for cursor positioning.
        // When template HTML contains a <br> in an empty paragraph, ProseMirror keeps it
        // as a hardBreak AND adds its own trailing break, causing double line spacing.
        // Hide the trailing break when it directly follows another <br> or a <span>
        // that wraps a <br> (e.g. <span style="font-family: ..."><br></span>).
        .ProseMirror {
            p {
                margin: 0;
            }

            br + br.ProseMirror-trailingBreak,
            span + br.ProseMirror-trailingBreak {
                display: none;
            }
        }

        .ProseMirror img {
            &.ProseMirror-selectednode {
                outline: 2px solid #3e8dbc;
            }
        }

        .tableWrapper[data-vehicle-brochure='true'] {
            overflow: visible;

            table {
                width: auto !important;
                min-width: 0 !important;
                border-collapse: separate !important;
                table-layout: auto !important;
            }
        }

        table[data-vehicle-brochure-root='true'] {
            width: auto !important;
            max-width: 347px;
            border-collapse: separate !important;
            table-layout: auto !important;
            border-spacing: 0 !important;
            box-shadow: 0 0 5px 2px #d9d9d9;
            border: 1px solid #d9d9d9;
            border-radius: 6px;
            overflow: visible;

            td,
            th {
                border: none !important;
                min-width: unset !important;
                background: transparent;
                position: static;
            }

            table {
                width: 100% !important;
            }

            img {
                max-width: 100%;
                height: auto;
            }

            colgroup,
            col {
                display: none !important;
                min-width: unset !important;
                width: auto !important;
            }
        }

        .vehicle-brochure-node {
            color: #1f2933;
        }
    }
</style>
